// Carrega e grava os horários (GET/PUT /api/business-hours). A tela mexe num
// rascunho; Salvar grava a semana inteira e Descartar volta ao último salvo.

import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { doServidor, paraServidor, temErro } from "./lib/horarios";

const API = "/api";
const cabecalho = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const copia = (x) => JSON.parse(JSON.stringify(x));

// Sem linha gravada para o dia (loja nova): o servidor já devolve os 7 dias,
// isto é só a rede de segurança — aberto das 08:00 às 23:00, como o servidor.
const diaPadrao = (dw) => ({ dw, aberto: true, h24: false, periodos: [["08:00", "23:00"]] });

export default function useHorarios() {
  const [originais, setOriginais] = useState({});
  const [salvo, setSalvo] = useState(null);
  const [rascunho, setRascunho] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erroCarga, setErroCarga] = useState(false);

  const carregar = useCallback(async () => {
    setErroCarga(false);
    try {
      const { data } = await axios.get(`${API}/business-hours`, cabecalho());
      const porDia = Object.fromEntries((data || []).map((h) => [h.day_of_week, h]));
      const dias = [0, 1, 2, 3, 4, 5, 6].map((dw) => (porDia[dw] ? doServidor(porDia[dw]) : diaPadrao(dw)));
      setOriginais(porDia);
      setSalvo(dias);
      setRascunho(copia(dias));
    } catch (e) {
      setErroCarga(true);
      toast.error("Erro ao carregar os horários de funcionamento");
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const alterado = useMemo(() => !!salvo && JSON.stringify(salvo) !== JSON.stringify(rascunho), [salvo, rascunho]);
  const comErro = useMemo(() => !!rascunho && temErro(rascunho), [rascunho]);

  /** Troca um dia do rascunho: `mudar(dia)` devolve o dia novo. */
  const mudarDia = useCallback((dw, mudar) => {
    setRascunho((r) => r.map((d) => (d.dw === dw ? mudar(copia(d)) : d)));
  }, []);

  /** Copia o horário de um dia para outros (lista de day_of_week). */
  const copiarDia = useCallback((de, para) => {
    setRascunho((r) => {
      const origem = r.find((d) => d.dw === de);
      return r.map((d) => (para.includes(d.dw) ? { ...copia(origem), dw: d.dw } : d));
    });
  }, []);

  const descartar = useCallback(() => setRascunho(copia(salvo)), [salvo]);

  const salvar = useCallback(async () => {
    if (comErro) { toast.error("Tem horário que fecha antes de abrir. Corrija o que está em vermelho."); return false; }
    setSalvando(true);
    try {
      const hours = rascunho.map((d) => paraServidor(d, originais[d.dw]));
      const { data } = await axios.put(`${API}/business-hours`, { hours }, cabecalho());
      // O que o servidor gravou vira o novo "salvo" (dia sem período volta como fechado)
      const porDia = { ...originais, ...Object.fromEntries((Array.isArray(data) ? data : []).map((h) => [h.day_of_week, h])) };
      const dias = rascunho.map((d) => (porDia[d.dw] && Array.isArray(data) && data.length ? doServidor(porDia[d.dw]) : d));
      setOriginais(porDia);
      setSalvo(dias);
      setRascunho(copia(dias));
      toast.success("Seu novo horário de funcionamento foi salvo.");
      return true;
    } catch (e) {
      const detalhe = e?.response?.data?.detail;
      toast.error(typeof detalhe === "string" ? detalhe : "Erro ao salvar os horários de funcionamento");
      return false;
    } finally {
      setSalvando(false);
    }
  }, [rascunho, originais, comErro]);

  return { rascunho, carregando: !rascunho && !erroCarga, erroCarga, carregar, alterado, comErro, salvando, mudarDia, copiarDia, descartar, salvar };
}
